import React, { useState, useEffect, useRef } from "react";
import { qcClient, QCJobResult, QCComment } from "../../api/qc_client";
import "./QCJobViewer.css";

interface QCJobViewerProps {
  job: QCJobResult;
  compact?: boolean;
}

const QCJobViewer: React.FC<QCJobViewerProps> = ({ job, compact = false }) => {
  const [currentJob, setCurrentJob] = useState(job);
  const [comments, setComments] = useState<QCComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const activeJobId = job.job_id;
    setCurrentJob(job);

    if (job.status !== "completed" && job.status !== "failed") {
      startPolling(activeJobId);
    } else {
      stopPolling();
    }

    fetchComments(activeJobId);

    return () => {
      stopPolling();
    };
  }, [job.job_id]);

  const startPolling = (jobId: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    setIsPolling(true);
    pollingRef.current = setInterval(() => {
      void pollJob(jobId);
    }, 2000);
  };

  const stopPolling = () => {
    setIsPolling(false);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const pollJob = async (jobId: string) => {
    try {
      const result = await qcClient.getJob(jobId);
      setCurrentJob(result);

      if (result.status === "completed" || result.status === "failed") {
        stopPolling();
        fetchComments(result.job_id);
      }
    } catch (err) {
      setError(`Polling error: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const fetchComments = async (jobId: string) => {
    try {
      const data = await qcClient.getComments(jobId);
      setComments(data);
    } catch (err) {
      // Comments might not be available yet
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const result = await qcClient.getJob(currentJob.job_id);
      setCurrentJob(result);
      await fetchComments(result.job_id);
    } catch (err) {
      setError(`Refresh error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const severityColor = (severity: string): string => {
    switch (severity) {
      case "critical":
        return "#dc2626";
      case "major":
        return "#ea580c";
      case "minor":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  };

  if (compact) {
    return (
      <div className="job-viewer compact">
        <div className="job-info-compact">
          <span className={`status status-${currentJob.status}`}>{currentJob.status}</span>
          {currentJob.qc_status && (
            <span className={`qc-status qc-${currentJob.qc_status}`}>{currentJob.qc_status}</span>
          )}
          {currentJob.processing_time_ms && (
            <span className="timing">{currentJob.processing_time_ms}ms</span>
          )}
          {currentJob.error && <span className="error-badge">Error: {currentJob.error}</span>}
          {currentJob.findings.length > 0 && (
            <span className="findings-count">{currentJob.findings.length} findings</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="job-viewer">
      {/* Header */}
      <div className="viewer-header">
        <div className="job-title">
          <h3>{currentJob.asset_name}</h3>
          <code className="job-id">{currentJob.job_id}</code>
        </div>
        <div className="viewer-actions">
          {isPolling && <span className="polling-indicator">🔄 Polling...</span>}
          <button onClick={handleRefresh} disabled={loading} className="btn-small">
            {loading ? "Refreshing..." : "🔄 Refresh"}
          </button>
          {isPolling && (
            <button onClick={stopPolling} className="btn-small btn-warning">
              ⏹ Stop Polling
            </button>
          )}
        </div>
      </div>

      {/* Status Overview */}
      <div className="status-overview">
        <div className="status-item">
          <label>Job Status:</label>
          <span className={`badge status-${currentJob.status}`}>{currentJob.status}</span>
        </div>
        {currentJob.qc_status && (
          <div className="status-item">
            <label>QC Result:</label>
            <span className={`badge qc-${currentJob.qc_status}`}>{currentJob.qc_status}</span>
          </div>
        )}
        <div className="status-item">
          <label>Media Type:</label>
          <span>{currentJob.media_type}</span>
        </div>
        {currentJob.processing_time_ms && (
          <div className="status-item">
            <label>Processing Time:</label>
            <span>{currentJob.processing_time_ms}ms</span>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && <div className="alert alert-error">{error}</div>}
      {currentJob.error && <div className="alert alert-error">Error: {currentJob.error}</div>}

      {/* Summary */}
      {(currentJob.display_summary || currentJob.summary) && (
        <div className="summary-section">
          <h4>Summary</h4>
          <p>{currentJob.display_summary || currentJob.summary}</p>
        </div>
      )}

      {/* Findings */}
      {currentJob.findings && currentJob.findings.length > 0 && (
        <div className="findings-section">
          <h4>Findings ({currentJob.findings.length})</h4>
          <div className="findings-list">
            {currentJob.findings.map((finding, idx) => {
              const headline = finding.title || finding.issue;
              const body = finding.detail || (finding.title ? "" : "");
              const audit = finding.audit;
              const hasTechnical = Boolean(
                audit && (audit.raw_issue || (audit.frame_indices && audit.frame_indices.length))
              );
              return (
                <div key={idx} className="finding-card" style={{ borderLeftColor: severityColor(finding.severity) }}>
                  <div className="finding-header">
                    <span className={`severity severity-${finding.severity}`}>
                      {finding.severity.toUpperCase()}
                    </span>
                    {finding.timestamp && <span className="timestamp">{finding.timestamp}</span>}
                    {finding.category && <span className="category">{finding.category}</span>}
                    {!finding.category && finding.policy_ref && (
                      <span className="policy-ref">{finding.policy_ref}</span>
                    )}
                  </div>
                  <p className="finding-issue"><strong>{headline}</strong></p>
                  {finding.policy_section && (
                    <p className="finding-policy-section">
                      <em>Policy: {finding.policy_section}</em>
                      {finding.code && <span className="finding-code"> ({finding.code})</span>}
                    </p>
                  )}
                  {body && <p className="finding-detail">{body}</p>}
                  {finding.recommended_action && (
                    <p className="finding-action">
                      <strong>Recommended action:</strong> {finding.recommended_action}
                    </p>
                  )}
                  {finding.mention && <p className="mention">Assigned to: {finding.mention}</p>}
                  {hasTechnical && (
                    <details className="finding-audit">
                      <summary>Show technical details</summary>
                      <ul>
                        {audit?.rule_id && <li><strong>Rule:</strong> {audit.rule_id}</li>}
                        {audit?.code && <li><strong>Code:</strong> {audit.code}</li>}
                        {audit?.tool && <li><strong>Tool:</strong> {audit.tool}</li>}
                        {audit?.override_kind && (
                          <li><strong>Override kind:</strong> {audit.override_kind}</li>
                        )}
                        {audit?.signals && audit.signals.length > 0 && (
                          <li><strong>Signals:</strong> {audit.signals.join(", ")}</li>
                        )}
                        {audit?.confidence_band && (
                          <li><strong>Confidence:</strong> {audit.confidence_band}</li>
                        )}
                        {audit?.frame_indices && audit.frame_indices.length > 0 && (
                          <li><strong>Frame indices:</strong> [{audit.frame_indices.join(", ")}]</li>
                        )}
                        {audit?.thresholds && (
                          <li><strong>Thresholds:</strong> {JSON.stringify(audit.thresholds)}</li>
                        )}
                        {audit?.raw_issue && (
                          <li><strong>Raw verifier output:</strong> <code>{audit.raw_issue}</code></li>
                        )}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments */}
      {comments && comments.length > 0 && (
        <div className="comments-section">
          <h4>Comments ({comments.length})</h4>
          <div className="comments-list">
            {comments.map((comment) => (
              <div key={comment.id} className="comment-card">
                <div className="comment-meta">
                  <strong>{comment.author.name}</strong>
                  {comment.timecode && <span className="timecode">{comment.timecode}</span>}
                  {comment.mention && <span className="mention">{comment.mention}</span>}
                  <span className={`severity severity-${comment.severity}`}>
                    {comment.severity}
                  </span>
                </div>
                <p>{comment.body}</p>
                {comment.policy_ref && <p className="policy-ref">Policy: {comment.policy_ref}</p>}
                <small className="timestamp">
                  {new Date(comment.created_at).toLocaleString()}
                </small>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw Analysis */}
      {currentJob.raw_analysis && (
        <div className="raw-analysis-section">
          <details>
            <summary>View Raw Analysis</summary>
            <pre className="raw-analysis">{currentJob.raw_analysis}</pre>
          </details>
        </div>
      )}

      {/* Metadata */}
      {currentJob.metadata && Object.keys(currentJob.metadata).length > 0 && (
        <div className="metadata-section">
          <details>
            <summary>View Metadata</summary>
            <pre>{JSON.stringify(currentJob.metadata, null, 2)}</pre>
          </details>
        </div>
      )}

      {/* Job Details */}
      <div className="details-section">
        <details>
          <summary>View Job Details</summary>
          <pre>{JSON.stringify(currentJob, null, 2)}</pre>
        </details>
      </div>
    </div>
  );
};

export default QCJobViewer;
